import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { DashboardStat } from './dashboard.types';

function formatValue(value: number, isMoney: boolean) {
  return isMoney ? formatCurrency(value) : formatNumber(value);
}

/** Compact "↑ 12.3%" — for table cells, next to the current value. */
export function GrowthBadge({ change }: { change: number | null | undefined }) {
  if (change === null || change === undefined) return null;
  const Icon = change === 0 ? Minus : change > 0 ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs whitespace-nowrap',
        change > 0 && 'text-success',
        change < 0 && 'text-destructive',
        change === 0 && 'text-muted-foreground'
      )}
    >
      <Icon className="size-3" />
      {Math.abs(change).toFixed(1)}%
    </span>
  );
}

/** A table cell's value plus its growth badge, right-aligned. */
export function StatCell({
  stat,
  isMoney,
}: {
  stat: DashboardStat | undefined;
  isMoney: boolean;
}) {
  if (!stat) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex items-center justify-end gap-1.5 tabular-nums">
      <span>{formatValue(stat.value, isMoney)}</span>
      <GrowthBadge change={stat.change} />
    </div>
  );
}

/** "↓ 54.0% (1,395)" — the delta line under a Sales stat row. */
export function GrowthCaption({
  stat,
  isMoney,
}: {
  stat: DashboardStat;
  isMoney: boolean;
}) {
  const previous = formatValue(stat.previousValue, isMoney);
  if (stat.change === null) return <span>{previous}</span>;
  const Icon = stat.change === 0 ? Minus : stat.change > 0 ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        stat.change > 0 && 'text-success',
        stat.change < 0 && 'text-destructive',
        stat.change === 0 && 'text-muted-foreground'
      )}
    >
      <Icon className="size-3.5" />
      {Math.abs(stat.change).toFixed(1)}%
      <span className="text-muted-foreground">({previous})</span>
    </span>
  );
}
